import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Award, Download, Eye, FileText, Loader2, CheckCircle, Search,
  GraduationCap, BookOpen, Shield, RefreshCw, Star, Clock, Printer
} from "lucide-react";

const TRACK_COLORS = {
  "ELNEC Core": "bg-purple-100 text-purple-800",
  "RN Case Manager": "bg-blue-100 text-blue-800",
  "Wound Care": "bg-red-100 text-red-800",
  "Hospice Aide": "bg-green-100 text-green-800",
  "Social Work": "bg-yellow-100 text-yellow-800",
  "Chaplaincy": "bg-indigo-100 text-indigo-800",
  "Fundamentals": "bg-teal-100 text-teal-800",
  "Pain Management": "bg-orange-100 text-orange-800",
  "Timely Care": "bg-cyan-100 text-cyan-800",
  "Communication": "bg-pink-100 text-pink-800",
  "Compliance": "bg-gray-100 text-gray-800"
};

export default function CEUCertificates() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("certificates");
  
  // Certificates
  const [certificates, setCertificates] = useState([]);
  const [totalCEU, setTotalCEU] = useState(0);
  
  // Available courses
  const [tracks, setTracks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Transcript
  const [transcript, setTranscript] = useState(null);
  
  // Preview dialog
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Verification
  const [verifyNumber, setVerifyNumber] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  
  // Generating
  const [generating, setGenerating] = useState({});

  const isStaff = ["admin", "administrator", "staff", "nurse", "coordinator", "physician"].includes(user?.role?.toLowerCase());

  useEffect(() => {
    if (authLoading) return;
    if (!isStaff) {
      toast.error("Staff access required");
      navigate("/");
      return;
    }
    loadData();
  }, [user, authLoading]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [certsRes, coursesRes] = await Promise.all([
        api.get("/academy/certificates/my-certificates").catch(() => ({ data: { certificates: [], total_ceu_earned: 0 } })),
        api.get("/academy/certificates/available-courses").catch(() => ({ data: { tracks: [] } }))
      ]);
      
      setCertificates(certsRes.data.certificates || []);
      setTotalCEU(certsRes.data.total_ceu_earned || 0);
      setTracks(coursesRes.data.tracks || []);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTranscript = async () => {
    try {
      const res = await api.get("/academy/certificates/transcript");
      setTranscript(res.data.transcript);
    } catch (error) {
      toast.error("Failed to load transcript");
    }
  };

  const generateCertificate = async (courseId) => {
    setGenerating({ ...generating, [courseId]: true });
    try {
      const res = await api.post(`/academy/certificates/generate?course_id=${courseId}`);
      if (res.data.success) {
        toast.success("Certificate generated!");
        loadData();
      } else {
        toast.info(res.data.message);
      }
    } catch (error) {
      toast.error("Failed to generate certificate");
    } finally {
      setGenerating({ ...generating, [courseId]: false });
    }
  };

  const downloadCertificate = async (certificateId, certNumber) => {
    try {
      const res = await api.get(`/academy/certificates/download/${certificateId}`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([res.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${certNumber}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Certificate downloaded! Open in browser and print to PDF.");
    } catch (error) {
      toast.error("Failed to download certificate");
    }
  };

  const previewCertificate = async (certificateId) => {
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const res = await api.get(`/academy/certificates/preview/${certificateId}`);
      setPreviewContent(res.data);
    } catch (error) {
      toast.error("Failed to load preview");
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const verifyCertificate = async () => {
    if (!verifyNumber.trim()) return;
    
    setVerifying(true);
    try {
      const res = await api.get(`/academy/certificates/verify/${verifyNumber}`);
      setVerifyResult(res.data);
    } catch (error) {
      setVerifyResult({ valid: false, message: "Verification failed" });
    } finally {
      setVerifying(false);
    }
  };

  const printCertificate = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(previewContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  // Filter courses by search
  const filteredTracks = tracks.map(track => ({
    ...track,
    courses: track.courses.filter(course => 
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      track.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(track => track.courses.length > 0);

  // Check if course is already certified
  const isCertified = (courseId) => {
    return certificates.some(c => c.course_id === courseId);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="ceu-certificates">
      <div className="container-max px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-serif font-medium text-foreground flex items-center gap-3">
              <Award className="w-8 h-8 text-primary" />
              CEU Certificates
            </h1>
            <p className="text-muted-foreground mt-1">
              Download and manage your training certificates
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-3xl font-bold text-primary">{totalCEU.toFixed(1)}</p>
              <p className="text-sm text-muted-foreground">Total CEUs Earned</p>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{certificates.length}</p>
                  <p className="text-xs text-muted-foreground">Certificates</p>
                </div>
                <Award className="w-6 h-6 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{totalCEU.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">CEUs Earned</p>
                </div>
                <Star className="w-6 h-6 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{tracks.length}</p>
                  <p className="text-xs text-muted-foreground">Training Tracks</p>
                </div>
                <GraduationCap className="w-6 h-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">
                    {tracks.reduce((sum, t) => sum + t.courses.length, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Courses</p>
                </div>
                <BookOpen className="w-6 h-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="certificates" className="gap-2">
              <Award className="w-4 h-4" />
              My Certificates
              {certificates.length > 0 && (
                <Badge variant="secondary">{certificates.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="generate" className="gap-2">
              <FileText className="w-4 h-4" />
              Generate Certificate
            </TabsTrigger>
            <TabsTrigger value="transcript" className="gap-2" onClick={loadTranscript}>
              <BookOpen className="w-4 h-4" />
              CEU Transcript
            </TabsTrigger>
            <TabsTrigger value="verify" className="gap-2">
              <Shield className="w-4 h-4" />
              Verify Certificate
            </TabsTrigger>
          </TabsList>

          {/* My Certificates Tab */}
          <TabsContent value="certificates">
            <Card>
              <CardHeader>
                <CardTitle>Your Earned Certificates</CardTitle>
                <CardDescription>
                  Download or print certificates for completed training courses
                </CardDescription>
              </CardHeader>
              <CardContent>
                {certificates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Award className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">No certificates yet</p>
                    <p className="text-sm">Complete courses in the Hospice Academy to earn certificates</p>
                    <Button className="mt-4" onClick={() => setActiveTab("generate")}>
                      Generate Certificate
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {certificates.map((cert) => (
                      <Card key={cert.id} className="border-l-4 border-l-primary">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-medium">{cert.course_title}</h3>
                              <Badge className={TRACK_COLORS[cert.course_track] || "bg-gray-100"}>
                                {cert.course_track}
                              </Badge>
                            </div>
                            <Badge variant="outline" className="text-lg font-bold">
                              {cert.ceu_credits} CEU
                            </Badge>
                          </div>
                          
                          <div className="space-y-1 text-sm text-muted-foreground mb-4">
                            <p className="flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              Completed: {cert.completion_date}
                            </p>
                            <p className="flex items-center gap-2">
                              <FileText className="w-3 h-3" />
                              Certificate #: {cert.certificate_number}
                            </p>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => previewCertificate(cert.id)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => downloadCertificate(cert.id, cert.certificate_number)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Generate Certificate Tab */}
          <TabsContent value="generate">
            <Card>
              <CardHeader>
                <CardTitle>Generate New Certificate</CardTitle>
                <CardDescription>
                  Select a completed course to generate a certificate
                </CardDescription>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search courses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  <div className="space-y-6">
                    {filteredTracks.map((track) => (
                      <div key={track.name}>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium flex items-center gap-2">
                            <Badge className={TRACK_COLORS[track.name] || "bg-gray-100"}>
                              {track.name}
                            </Badge>
                          </h3>
                          <span className="text-sm text-muted-foreground">
                            {track.total_ceu} CEUs available
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {track.courses.map((course) => {
                            const certified = isCertified(course.id);
                            return (
                              <div 
                                key={course.id}
                                className={`p-3 border rounded-lg ${
                                  certified ? "bg-green-50 border-green-200" : ""
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{course.title}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {course.ceu} CEU credits
                                    </p>
                                  </div>
                                  {certified ? (
                                    <Badge className="bg-green-100 text-green-800">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Certified
                                    </Badge>
                                  ) : (
                                    <Button 
                                      size="sm"
                                      disabled={generating[course.id]}
                                      onClick={() => generateCertificate(course.id)}
                                    >
                                      {generating[course.id] ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        "Generate"
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transcript Tab */}
          <TabsContent value="transcript">
            <Card>
              <CardHeader>
                <CardTitle>CEU Transcript</CardTitle>
                <CardDescription>
                  Official record of all earned continuing education credits
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!transcript ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                    <p className="mt-2 text-muted-foreground">Loading transcript...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="p-6 bg-muted/50 rounded-lg text-center">
                      <h2 className="text-2xl font-bold">{transcript.user_name}</h2>
                      <p className="text-muted-foreground">{transcript.user_email}</p>
                      <div className="mt-4 flex justify-center gap-8">
                        <div>
                          <p className="text-3xl font-bold text-primary">{transcript.total_ceu_earned}</p>
                          <p className="text-sm text-muted-foreground">Total CEUs</p>
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-primary">{transcript.total_certificates}</p>
                          <p className="text-sm text-muted-foreground">Certificates</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">
                        Generated: {transcript.generated_date}
                      </p>
                    </div>

                    {/* By Track */}
                    {Object.entries(transcript.by_track || {}).map(([trackName, trackData]) => (
                      <div key={trackName} className="border rounded-lg">
                        <div className="p-4 bg-muted/30 border-b flex items-center justify-between">
                          <Badge className={TRACK_COLORS[trackName] || "bg-gray-100"}>
                            {trackName}
                          </Badge>
                          <span className="font-bold">{trackData.total_ceu} CEUs</span>
                        </div>
                        <div className="divide-y">
                          {trackData.courses.map((course) => (
                            <div key={course.id} className="p-3 flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm">{course.course_title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {course.completion_date} • #{course.certificate_number}
                                </p>
                              </div>
                              <Badge variant="outline">{course.ceu_credits} CEU</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verify Tab */}
          <TabsContent value="verify">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Verify Certificate
                </CardTitle>
                <CardDescription>
                  Enter a certificate number to verify its authenticity
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-md mx-auto space-y-4">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Enter certificate number (e.g., GW-20260115-ABC12345)"
                      value={verifyNumber}
                      onChange={(e) => setVerifyNumber(e.target.value.toUpperCase())}
                    />
                    <Button onClick={verifyCertificate} disabled={verifying}>
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
                    </Button>
                  </div>

                  {verifyResult && (
                    <div className={`p-4 rounded-lg ${
                      verifyResult.valid 
                        ? "bg-green-50 border border-green-200" 
                        : "bg-red-50 border border-red-200"
                    }`}>
                      {verifyResult.valid ? (
                        <>
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="w-6 h-6 text-green-500" />
                            <span className="font-medium text-green-800">Valid Certificate</span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <p><strong>Name:</strong> {verifyResult.certificate?.user_name}</p>
                            <p><strong>Course:</strong> {verifyResult.certificate?.course_title}</p>
                            <p><strong>Track:</strong> {verifyResult.certificate?.course_track}</p>
                            <p><strong>CEUs:</strong> {verifyResult.certificate?.ceu_credits}</p>
                            <p><strong>Completed:</strong> {verifyResult.certificate?.completion_date}</p>
                            <p><strong>Issued:</strong> {formatDate(verifyResult.certificate?.issued_at)}</p>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <X className="w-6 h-6 text-red-500" />
                          <span className="text-red-800">Certificate not found or invalid</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Certificate Preview</DialogTitle>
            </DialogHeader>
            {previewLoading ? (
              <div className="h-[600px] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="overflow-auto max-h-[600px]">
                <iframe
                  srcDoc={previewContent}
                  className="w-full h-[600px] border rounded"
                  title="Certificate Preview"
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Close
              </Button>
              <Button variant="outline" onClick={printCertificate}>
                <Printer className="w-4 h-4 mr-2" />
                Print to PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// X icon component
function X({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
    </svg>
  );
}
